import React, { useEffect, useRef, useState } from 'react';
import '../assets/css/systemManagement.css';
import { RiderServicesModel } from '../models/RiderServicesModel';
import {
    FilterDataModel,
    SortModel,
    SortDirection,
    RiderServiceModel,
    RiderComapnyModel,
    RiderServiceBranch,
} from '../models/superbit-api-models';
import DeleteAlert from '../components/DeleteAlert';
import NewRiderService from './_NewRiderService';

// React port of Views/SystemManagement/Riders/_RiderServices.cshtml (+ the
// _RiderServicesItems.cshtml grid rows and the sortRiderServices / openRiderService
// / deleteRiderService handlers from wwwroot/js/systemManagement.js).
// The grid is a matrix: each row is a rider service; the fixed columns are the
// service name (שם השירות) and branch (ענף), and there is one dynamic column per
// rider company showing that company's broadcast code for the service.
// The server (riderSortService -> RiderBL) applies the optional search term and
// sort but does NOT paginate, so the whole list renders at once.

// Fixed (non-company) sortable columns. `sortBy` matches the property names the
// old MVC view sent via data-sort-by so the existing server-side sorting keeps
// working. The per-company columns are appended dynamically and are not sortable.
type Column = { key: string; label: string; sortBy?: string };
const FIXED_COLUMNS: Column[] = [
    { key: 'title', label: 'שם השירות', sortBy: 'Title' },
    { key: 'branch', label: 'ענף', sortBy: 'RiderServiceBranch' },
];

// RiderServiceBranch display names — mirrors the [Display(Name=...)] attributes on
// the C# enum (same mapping the rider-packages tab uses).
const branchName = (b?: RiderServiceBranch) => (b === RiderServiceBranch._1 ? 'דירה' : 'רכב');

// The broadcast code this service carries for a given company, or '' if none.
const broadcastCodeFor = (service: RiderServiceModel, companyId?: string): string => {
    if (!companyId) return '';
    const match = (service.riderServiceCompanies || []).find(
        (c) => c.riderComapnyId === companyId
    );
    return (match && match.broadcastCode) || '';
};

const RiderServicesManagement = () => {
    const [servicesModel] = useState<RiderServicesModel>(() => new RiderServicesModel());

    const [services, setServices] = useState<RiderServiceModel[]>([]);
    const [companies, setCompanies] = useState<RiderComapnyModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);
    const [reloadTick, setReloadTick] = useState<number>(0); // bumped to force a reload (e.g. on refresh/clear)

    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit modal. editId set = edit; undefined while the modal is open = add.
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editId, setEditId] = useState<string | undefined>(undefined);

    const buildFilter = (): FilterDataModel => {
        const filter = new FilterDataModel();
        filter.searchTerm = searchTerm ? searchTerm : undefined;
        // The API (RiderBL.SortService) only applies SearchTerm when SortBy is
        // non-null — with a null SortBy it returns every service and ignores the
        // search. So always send a sort, defaulting to Title ascending when the
        // user hasn't picked a column; otherwise typed searches do nothing.
        const sort = new SortModel();
        sort.property = sortProp || 'Title';
        sort.sortDirection = sortProp ? sortDir : SortDirection._0;
        filter.sortBy = [sort];
        return filter;
    };

    const load = async () => {
        setLoading(true);
        try {
            const result = await servicesModel.sortServices(buildFilter());
            setServices(result && result.riderServices ? result.riderServices : []);
            setCompanies(result && result.riderComapnies ? result.riderComapnies : []);
        } catch (e) {
            console.error('Failed to load rider services', e);
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    // Reload whenever sort changes (reloadTick forces a reload).
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortProp, sortDir, reloadTick]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') load();
    };

    // Refresh button: clear the search filter and reload (reloadTick guarantees the reload).
    const clearFilters = () => {
        setSearchTerm('');
        setReloadTick((t) => t + 1);
    };

    // Auto-search: reload shortly after the user stops typing (skip the initial mount).
    const searchDidMount = useRef(false);
    useEffect(() => {
        if (!searchDidMount.current) {
            searchDidMount.current = true;
            return;
        }
        const t = setTimeout(() => load(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const toggleMenu = (id?: string) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    const onAdd = () => {
        setEditId(undefined);
        setShowModal(true);
    };

    const onEdit = (service: RiderServiceModel) => {
        setOpenMenuId(undefined);
        setEditId(service.id);
        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await servicesModel.deleteService(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete rider service', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    // Total column count for the empty-state colSpan: fixed + per-company + actions.
    const totalCols = FIXED_COLUMNS.length + companies.length + 1;

    return (
        <div className="users-management rtl">
            <div className="users-toolbar flex-centered">
                <div className="users-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="users-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                </div>
                <div className="users-refresh pointer" title="רענון" onClick={() => clearFilters()} />
            </div>

            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {FIXED_COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            {/* One column per rider company (shows the broadcast code). */}
                            {companies.map((comp) => (
                                <th key={comp.id} className="users-th">
                                    <span>{comp.name}</span>
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAdd}>
                                    <span className="add-file" />
                                    <span className="text">הוסף</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {services.map((service) => (
                            <tr key={service.id}>
                                <td className="bold">{service.title}</td>
                                <td>{branchName(service.riderServiceBranch)}</td>
                                {companies.map((comp) => (
                                    <td key={comp.id}>{broadcastCodeFor(service, comp.id)}</td>
                                ))}
                                <td className="users-actions">
                                    <button
                                        type="button"
                                        className="actionBtn pointer"
                                        onClick={() => toggleMenu(service.id)}
                                    >
                                        <div className="edit-icon" />
                                        <span>פעולות</span>
                                        <div className="dropdown-icon" />
                                    </button>
                                    {openMenuId === service.id && (
                                        <ul className="actionBtnMenu">
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => onEdit(service)}
                                            >
                                                ערוך
                                            </li>
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => {
                                                    setOpenMenuId(undefined);
                                                    setDeleteId(service.id);
                                                }}
                                            >
                                                מחק
                                            </li>
                                        </ul>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!loading && services.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={totalCols}>
                                    לא נמצאו שירותים
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            {showModal && (
                <NewRiderService
                    show={showModal}
                    serviceId={editId}
                    onClose={() => setShowModal(false)}
                    onSaved={() => {
                        setShowModal(false);
                        load();
                    }}
                />
            )}
        </div>
    );
};

export default RiderServicesManagement;
export {};
